import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Bell, CheckCheck, Sparkles, Flame, Gift, History, ClipboardList,
  Trophy, Users as UsersIcon, Tag, Trash2, Loader2,
} from "lucide-react";

const CATEGORY_ICON = {
  reminders: ClipboardList,
  new_features: Sparkles,
  cpg_deals: Tag,
  streaks: Flame,
  blueprint_score: Trophy,
  weekly_digest: History,
  team_invites: UsersIcon,
};

const CATEGORY_LABEL = {
  reminders: "Reminder",
  new_features: "New feature",
  cpg_deals: "CPG deal",
  streaks: "Streak",
  blueprint_score: "Blueprint",
  weekly_digest: "Weekly digest",
  team_invites: "Team",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * NotificationsBell — top-right bell dropdown with a scrollable feed +
 * mark-as-read affordances. Polls every 60s for unread count.
 */
export default function NotificationsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const ref = useRef(null);

  const loadFeed = useCallback(() => {
    setLoading(true);
    api.get("/notifications", { params: { limit: 25 } })
      .then((r) => {
        setRows(r.data.notifications || []);
        setUnread(r.data.unread_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadUnread = useCallback(() => {
    api.get("/notifications/unread-count")
      .then((r) => setUnread(r.data.unread_count || 0))
      .catch(() => {});
  }, []);

  // Poll unread count every 60s (light-touch)
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 60000);
    return () => clearInterval(t);
  }, [loadUnread]);

  // Load full feed when dropdown opens
  useEffect(() => { if (open) loadFeed(); }, [open, loadFeed]);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const markOneRead = async (n) => {
    if (n.read) return;
    setBusyId(n.id);
    try {
      await api.post(`/notifications/${n.id}/read`);
      setRows((prev) => prev.map((r) => r.id === n.id ? { ...r, read: true } : r));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
    finally { setBusyId(null); }
  };

  const markAll = async () => {
    if (unread === 0) return;
    setLoading(true);
    try {
      await api.post("/notifications/read-all");
      setRows((prev) => prev.map((r) => ({ ...r, read: true })));
      setUnread(0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const deleteOne = async (n, e) => {
    e.stopPropagation();
    setBusyId(n.id);
    try {
      await api.delete(`/notifications/${n.id}`);
      setRows((prev) => prev.filter((r) => r.id !== n.id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
    finally { setBusyId(null); }
  };

  const openItem = async (n) => {
    await markOneRead(n);
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={unread ? `${unread} unread` : "Notifications"}
        data-testid="topbar-notifications"
        className="p-2 rounded hover:bg-muted relative"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="topbar-notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 grid place-items-center text-[9px] font-bold rounded-full bg-primary text-white leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="notifications-panel"
          className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-auto sm:mt-2 sm:w-[380px] max-h-[calc(100vh-5rem)] sm:max-h-[520px] rounded-xl border border-shell-border bg-white shadow-xl z-40 overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-shell-border bg-shell-muted/40">
            <div>
              <p className="font-semibold text-sm">Notifications</p>
              <p className="text-[11px] text-muted-foreground">
                {unread ? `${unread} unread` : "All caught up"}
              </p>
            </div>
            <button
              type="button"
              onClick={markAll}
              disabled={unread === 0 || loading}
              data-testid="notifications-mark-all"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && rows.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center" data-testid="notifications-empty">
                <Bell className="w-6 h-6 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm font-semibold text-foreground">You're all caught up</p>
                <p className="text-[12px] text-muted-foreground mt-1">
                  New notifications will land here as they happen.
                </p>
              </div>
            )}
            <ul>
              {rows.map((n) => {
                const Icon = CATEGORY_ICON[n.category] || Bell;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      data-testid={`notification-${n.id}`}
                      className={[
                        "w-full text-left flex gap-3 px-4 py-3 border-b border-shell-border/60 hover:bg-shell-muted/50 transition",
                        n.read ? "" : "bg-primary/5",
                      ].join(" ")}
                    >
                      <div className={`w-8 h-8 shrink-0 rounded-lg grid place-items-center ${
                        n.read ? "bg-shell-muted text-muted-foreground" : "bg-primary/15 text-primary"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {!n.read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />}
                          <p className={`text-sm truncate ${n.read ? "text-muted-foreground" : "font-semibold text-foreground"}`}>
                            {n.title}
                          </p>
                        </div>
                        {n.body && (
                          <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 mt-1">
                          {CATEGORY_LABEL[n.category] || n.category} · {timeAgo(n.created_at)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => deleteOne(n, e)}
                        disabled={busyId === n.id}
                        title="Dismiss"
                        data-testid={`notification-dismiss-${n.id}`}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/60 hover:text-rose-600 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="border-t border-shell-border px-4 py-2 text-center bg-shell-muted/30">
            <button
              type="button"
              onClick={() => { setOpen(false); nav("/account/notifications"); }}
              data-testid="notifications-manage"
              className="text-[12px] font-semibold text-primary hover:underline"
            >
              Manage notification preferences →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
